<template>
  <div class="step">
    <div class="step-header">
      <div class="step-icon">
        <v-icon size="24" color="#e8612f">mdi-lock-outline</v-icon>
      </div>
      <div class="step-content">
        <h2 class="title">Set a new password</h2>
        <p class="desc">
          Create a strong password to keep your account secure.
        </p>
      </div>
    </div>

    <v-form class="form">
      <AppTextField v-model="newPassword" :type="showNewPassword ? 'text' : 'password'" autocomplete="new-password"
        label="New password"
        label-icon="mdi-lock-outline"
        :append-inner-icon="showNewPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'" placeholder="Enter new password"
        :error-messages="newPasswordError" @update:modelValue="newPasswordError = ''"
        @click:append-inner="showNewPassword = !showNewPassword" />

      <AppTextField v-model="confirmPassword" :type="showConfirmPassword ? 'text' : 'password'"
        autocomplete="new-password" 
        label="Confirm new password"
        label-icon="mdi-lock-check-outline"
        :append-inner-icon="showConfirmPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
        placeholder="Re-enter new password" :error-messages="confirmPasswordError"
        @update:modelValue="confirmPasswordError = ''" @blur="validateMatch"
        @click:append-inner="showConfirmPassword = !showConfirmPassword" />
    </v-form>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";

const props = defineProps<{
  current_step?: number;
  errors?: Record<string, string> | null;
  profileInfo?: any;
}>();

const newPassword = ref("");
const confirmPassword = ref("");
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);
const newPasswordError = ref("");
const confirmPasswordError = ref("");

function getValues() {
  return {
    newPassword: newPassword.value,
    confirmPassword: confirmPassword.value,
  };
}

defineExpose({ getValues });

watch(() => props.errors, (errs: any) => {
  newPasswordError.value = "";
  confirmPasswordError.value = "";
  if (!errs) return;
  if (errs.password) newPasswordError.value = errs.password;
  if (errs.password_confirmation) confirmPasswordError.value = errs.password_confirmation;
}, { immediate: true });

function validateMatch() {
  confirmPasswordError.value = "";
  if (!confirmPassword.value) return;
  if (confirmPassword.value !== newPassword.value) {
    confirmPasswordError.value = "Passwords do not match.";
  }
}
</script>

<style scoped>
.step {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.step-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 8px;
}

.step-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: var(--radius-lg);
  background: rgba(232, 97, 47, 0.1);
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 4px;
  letter-spacing: -0.025em;
}

.desc {
  margin: 0;
  color: var(--color-subtitle-light);
  font-size: 14px;
  line-height: 1.2;
  font-weight: 400;
}

.form {
  display: grid;
  gap: 20px;
  margin-top: 8px;
}
</style>
